import React, { useState, useEffect, useCallback } from 'react';
import { smtpConfigsService } from '../services/api';
import CascaDeModal from './shared/CascaDeModal';
import { useConfirmarPerigo, useConfirm } from './shared/ConfirmDialog';
import { useAviso } from './shared/Aviso';
import { campo, useEstadoDaCarga, EstadoDaLista, A_PROPRIA_LISTA_DIZ_O_VAZIO } from '../design';
import { finalidadesRepetidas } from '../utils/email';

const Icon = ({ name, className = 'w-5 h-5' }: { name: string; className?: string }) => {
  const icons: any = {
    mail: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' /></svg>,
    plus: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 4v16m8-8H4' /></svg>,
    edit: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' /></svg>,
    trash: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' /></svg>,
    check: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' /></svg>,
    x: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' /></svg>,
    refresh: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15' /></svg>,
  };
  return icons[name] || null;
};

interface SmtpConfig {
  id: number;
  name: string;
  transportType?: 'smtp' | 'sendgrid';
  host: string;
  port: number;
  user: string;
  password: string;
  fromEmail: string;
  fromName: string;
  secure: boolean;
  isActive: boolean;
  purposes: string[];
  notes: string;
  createdAt: string;
  updatedAt: string;
}

const smtpPurposeLabels: Record<string, string> = {
  'faturas': 'Faturamento',
  'propostas': 'Propostas',
  'contratos': 'Contratos',
  'financeiro': 'Financeiro',
  'licitacoes': 'Licitações',
  'sistema': 'Sistema',
  'geral': 'Geral',
};

export default function EmailAccountsTab() {
  const confirmarPerigo = useConfirmarPerigo();
  const confirmar = useConfirm();
  // D8 (11/10/2026): os 8 alert() viram o aviso do HUB.
  const avisar = useAviso();
  // D6: a lista calava; com ela fora, a tela dizia "Nenhuma conta" e oferecia "Nova Conta" (finalidade duplicada).
  const carga = useEstadoDaCarga();
  const rodarCarga = carga.rodar;
  const [smtpConfigs, setSmtpConfigs] = useState<SmtpConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [smtpModal, setSmtpModal] = useState<'create' | 'edit' | null>(null);
  const [selectedSmtp, setSelectedSmtp] = useState<SmtpConfig | null>(null);
  const [testingSmtp, setTestingSmtp] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const emptySmtpForm = {
    name: '',
    host: 'smtp.gmail.com',
    port: 587,
    user: '',
    password: '',
    fromEmail: '',
    fromName: 'IPCOM Tecnologia',
    secure: true,
    isActive: true,
    purposes: ['geral'] as string[],
    notes: '',
  };
  const [smtpForm, setSmtpForm] = useState<any>(emptySmtpForm);

  const loadData = useCallback(async () => {
    setLoading(true);
    await rodarCarga(async () => {
      const smtpData = await smtpConfigsService.getAll();
      setSmtpConfigs(Array.isArray(smtpData) ? smtpData : []);
    });
    setLoading(false);
  }, [rodarCarga]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateSmtp = () => {
    setSmtpForm(emptySmtpForm);
    setSelectedSmtp(null);
    setSmtpModal('create');
  };

  const handleEditSmtp = (smtp: SmtpConfig) => {
    setSmtpForm({
      name: smtp.name,
      host: smtp.host,
      port: smtp.port,
      user: smtp.user,
      password: smtp.password,
      fromEmail: smtp.fromEmail,
      fromName: smtp.fromName || '',
      secure: smtp.secure,
      isActive: smtp.isActive,
      purposes: smtp.purposes || ['geral'],
      notes: smtp.notes || '',
    });
    setSelectedSmtp(smtp);
    setSmtpModal('edit');
  };

  const handleSaveSmtp = async () => {
    // D6: duas contas ativas na mesma finalidade = remetente imprevisível (o servidor escolhe sem ordem).
    const repetida = finalidadesRepetidas(smtpForm, selectedSmtp, smtpConfigs, (x) => smtpPurposeLabels[x] || x);
    if (repetida && !(await confirmar({ title: 'Finalidade em duas contas', message: repetida, confirmLabel: 'Salvar assim mesmo', danger: true }))) return;
    setSaving(true);
    try {
      if (selectedSmtp) {
        await smtpConfigsService.update(selectedSmtp.id, smtpForm);
      } else {
        await smtpConfigsService.create(smtpForm);
      }
      setSmtpModal(null);
      loadData();
    } catch (e: any) {
      avisar('Erro ao salvar: ' + (e.response?.data?.message || e.message), 'error');
    }
    setSaving(false);
  };

  const handleDeleteSmtp = async (smtp: SmtpConfig) => {
    if (!await confirmarPerigo(`Deseja excluir a conta SMTP "${smtp.name}"?\n\nO envio não para: os e-mails que usavam esta conta passam a sair por outra conta ativa (a de finalidade geral, se houver), com o remetente dela.`)) return;
    try {
      await smtpConfigsService.delete(smtp.id);
      loadData();
    } catch (e: any) {
      // 404 = já não existe: o estado desejado é o estado atual. Ver ProposalsTab.
      if (e?.response?.status === 404) { loadData(); return; }
      avisar('Erro ao excluir: ' + (e.response?.data?.message || e.message), 'error');
    }
  };

  const handleTestSmtp = async (smtp: SmtpConfig) => {
    setTestingSmtp(smtp.id);
    try {
      const result = await smtpConfigsService.testConnection(smtp.id);
      if (result.success) {
        avisar('Conexão estabelecida com sucesso!', 'success');
      } else {
        avisar('Erro na conexão: ' + result.message, 'error');
      }
    } catch (e: any) {
      avisar('Erro ao testar: ' + (e.response?.data?.message || e.message), 'error');
    }
    setTestingSmtp(null);
  };

  const handleMigrateSmtp = async () => {
    try {
      const result = await smtpConfigsService.migrateFromEnv();
      if (result) {
        avisar('Configuração migrada com sucesso!', 'success');
        loadData();
      } else {
        avisar('Nenhuma configuração SMTP encontrada no ambiente', 'warning');
      }
    } catch (e: any) {
      avisar('Erro ao migrar: ' + (e.response?.data?.message || e.message), 'error');
    }
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center h-32'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500'></div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <div className='bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden'>
        <div className='p-4 border-b border-slate-800 flex items-center justify-between'>
          <div className='flex items-center gap-3'>
            <div>
              <h3 className='text-white font-semibold'>Contas de Email</h3>
              <p className='text-slate-400 text-sm'>Gerenciar contas SMTP e SendGrid para envio de emails</p>
            </div>
          </div>
          <div className='flex gap-2'>
            {carga.estado === 'pronto' && smtpConfigs.length === 0 && (
              <button
                onClick={handleMigrateSmtp}
                className='px-3 py-1.5 text-xs text-slate-300 bg-slate-700 rounded-lg hover:bg-slate-600'
              >
                Importar do Ambiente
              </button>
            )}
            {carga.estado === 'pronto' && <button
              onClick={handleCreateSmtp}
              className='flex items-center gap-1 px-3 py-1.5 text-xs text-white bg-success rounded-lg hover:bg-success-strong'
            >
              <Icon name='plus' className='w-3 h-3' />
              Nova Conta
            </button>}
          </div>
        </div>

        <div className='p-6'>
          <EstadoDaLista carga={carga} temDados={carga.atualizadoEm !== null} oQue='as contas de e-mail' onTentarDeNovo={loadData} vazio={A_PROPRIA_LISTA_DIZ_O_VAZIO}>
          {smtpConfigs.length === 0 ? (
            <div className='text-center py-6 bg-slate-800 rounded-lg border border-slate-700'>
              <Icon name='mail' className='w-10 h-10 mx-auto mb-2 text-slate-600' />
              <p className='text-slate-500 text-sm'>Nenhuma conta de email configurada</p>
              <p className='text-slate-600 text-xs mt-1'>Clique em "Importar do Ambiente" ou "Nova Conta"</p>
            </div>
          ) : (
            <div className='space-y-2'>
              {smtpConfigs.map((smtp) => (
                <div key={smtp.id} className='bg-slate-800 rounded-lg p-3 border border-slate-700'>
                  <div className='flex items-center justify-between'>
                    <div className='flex items-center gap-3'>
                      <div className={`p-2 rounded-lg ${smtp.transportType === 'sendgrid' ? 'bg-blue-500/10' : 'bg-green-500/10'}`}>
                        <Icon name='mail' className={`w-4 h-4 ${smtp.transportType === 'sendgrid' ? 'text-blue-400' : 'text-green-400'}`} />
                      </div>
                      <div>
                        <div className='flex items-center gap-2'>
                          <span className='text-white font-medium'>{smtp.name}</span>
                          {smtp.transportType === 'sendgrid' && (
                            <span className='px-2 py-0.5 text-xs bg-blue-500/20 text-blue-400 rounded font-medium'>SendGrid</span>
                          )}
                          <span className={`px-2 py-0.5 text-xs rounded ${smtp.isActive ? 'bg-green-500/20 text-green-400' : 'bg-slate-500/20 text-slate-400'}`}>
                            {smtp.isActive ? 'Ativo' : 'Inativo'}
                          </span>
                          {(smtp.purposes || []).map((p) => (
                            <span key={p} className='px-2 py-0.5 text-xs bg-slate-700 text-slate-300 rounded'>
                              {smtpPurposeLabels[p] || p}
                            </span>
                          ))}
                        </div>
                        <div className='flex items-center gap-3 mt-1 text-xs text-slate-500'>
                          {smtp.transportType === 'sendgrid' ? (
                            <>
                              <span>{smtp.fromEmail}</span>
                              {smtp.fromName && (
                                <>
                                  <span className='text-slate-600'>•</span>
                                  <span>De: {smtp.fromName}</span>
                                </>
                              )}
                            </>
                          ) : (
                            <>
                              <span>{smtp.user}</span>
                              <span className='text-slate-600'>•</span>
                              <span>{smtp.host}:{smtp.port}</span>
                              {smtp.fromName && (
                                <>
                                  <span className='text-slate-600'>•</span>
                                  <span>De: {smtp.fromName}</span>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className='flex items-center gap-1'>
                      <button
                        onClick={() => handleTestSmtp(smtp)}
                        disabled={testingSmtp === smtp.id}
                        className='p-2 text-slate-400 hover:text-green-400 hover:bg-slate-700 rounded-lg disabled:opacity-50'
                        title='Testar Conexão'
                      >
                        {testingSmtp === smtp.id ? (
                          <Icon name='refresh' className='w-4 h-4 animate-spin' />
                        ) : (
                          <Icon name='check' className='w-4 h-4' />
                        )}
                      </button>
                      <button
                        onClick={() => handleEditSmtp(smtp)}
                        className='p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg'
                        title='Editar'
                      >
                        <Icon name='edit' className='w-4 h-4' />
                      </button>
                      <button
                        onClick={() => handleDeleteSmtp(smtp)}
                        className='p-2 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded-lg'
                        title='Excluir'
                      >
                        <Icon name='trash' className='w-4 h-4' />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          </EstadoDaLista>
        </div>
      </div>

      {/* Modal SMTP Create/Edit */}
      {smtpModal && (
        // Formulário: não fecha ao clicar fora, como já era. O cabeçalho e o rodapé
        // "sticky" viram os da casca, que já ficam fora da rolagem.
        <CascaDeModal
          largura='sm'
          titulo={smtpModal === 'create' ? 'Nova Conta SMTP' : selectedSmtp?.transportType === 'sendgrid' ? 'Editar Conta SendGrid' : 'Editar Conta SMTP'}
          onClose={() => setSmtpModal(null)}
          corpoClassName='p-6 space-y-4'
          rodape={
            <div className='ml-auto flex gap-3'>
              <button
                onClick={() => setSmtpModal(null)}
                className='px-4 py-2.5 text-sm font-medium text-slate-300 bg-slate-700 rounded-lg hover:bg-slate-600'
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveSmtp}
                disabled={saving || !smtpForm.name || (selectedSmtp?.transportType !== 'sendgrid' && (!smtpForm.host || !smtpForm.user)) || (selectedSmtp?.transportType === 'sendgrid' && !smtpForm.fromEmail) || (!selectedSmtp && !smtpForm.password)}
                className='px-4 py-2.5 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-strong disabled:opacity-50'
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          }
        >
              <div>
                <label className='block text-sm font-medium text-slate-300 mb-2'>Nome da Conta *</label>
                <input
                  type='text'
                  value={smtpForm.name}
                  onChange={(e) => setSmtpForm({ ...smtpForm, name: e.target.value })}
                  placeholder='Ex: Email Faturas, Email Comercial'
                  className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`}
                />
              </div>

              {selectedSmtp?.transportType !== 'sendgrid' && (
                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <label className='block text-sm font-medium text-slate-300 mb-2'>Servidor SMTP *</label>
                    <input
                      type='text'
                      value={smtpForm.host}
                      onChange={(e) => setSmtpForm({ ...smtpForm, host: e.target.value })}
                      placeholder='smtp.gmail.com'
                      className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`}
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-slate-300 mb-2'>Porta *</label>
                    <input
                      type='number'
                      value={smtpForm.port}
                      onChange={(e) => setSmtpForm({ ...smtpForm, port: parseInt(e.target.value) || 587 })}
                      placeholder='587'
                      className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`}
                    />
                  </div>
                </div>
              )}

              {selectedSmtp?.transportType !== 'sendgrid' ? (
                <div>
                  <label className='block text-sm font-medium text-slate-300 mb-2'>Email/Usuário *</label>
                  <input
                    type='email'
                    value={smtpForm.user}
                    onChange={(e) => setSmtpForm({ ...smtpForm, user: e.target.value, fromEmail: e.target.value })}
                    placeholder='adm@ipcom.com.br'
                    className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`}
                  />
                </div>
              ) : (
                <div>
                  <label className='block text-sm font-medium text-slate-300 mb-2'>Email Remetente *</label>
                  <input
                    type='email'
                    value={smtpForm.fromEmail}
                    onChange={(e) => setSmtpForm({ ...smtpForm, fromEmail: e.target.value })}
                    placeholder='adm@ipcom.com.br'
                    className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`}
                  />
                </div>
              )}

              <div>
                <label className='block text-sm font-medium text-slate-300 mb-2'>
                  {selectedSmtp?.transportType === 'sendgrid' ? 'API Key' : 'Senha'} {selectedSmtp ? '(deixe em branco para manter)' : '*'}
                </label>
                <input
                  type='password'
                  value={smtpForm.password}
                  onChange={(e) => setSmtpForm({ ...smtpForm, password: e.target.value })}
                  placeholder={selectedSmtp ? '********' : selectedSmtp?.transportType === 'sendgrid' ? 'SG.xxxx...' : 'Senha ou App Password'}
                  className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`}
                />
                {selectedSmtp?.transportType !== 'sendgrid' && (
                  <p className='text-xs text-slate-500 mt-1'>
                    Para Gmail, use uma "Senha de App" gerada em myaccount.google.com
                  </p>
                )}
              </div>

              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label className='block text-sm font-medium text-slate-300 mb-2'>Nome do Remetente</label>
                  <input
                    type='text'
                    value={smtpForm.fromName}
                    onChange={(e) => setSmtpForm({ ...smtpForm, fromName: e.target.value })}
                    placeholder='IPCOM Tecnologia'
                    className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`}
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium text-slate-300 mb-2'>Usar para *</label>
                  <div className='space-y-2 bg-central-fundo border border-slate-600 rounded-lg p-3'>
                    {Object.entries(smtpPurposeLabels).map(([value, label]) => (
                      <label key={value} className='flex items-center gap-2 text-sm text-slate-300 cursor-pointer'>
                        <input
                          type='checkbox'
                          checked={(smtpForm.purposes || []).includes(value)}
                          onChange={(e) => {
                            const current: string[] = smtpForm.purposes || [];
                            const updated = e.target.checked
                              ? [...current, value]
                              : current.filter((p: string) => p !== value);
                            setSmtpForm({ ...smtpForm, purposes: updated.length > 0 ? updated : ['geral'] });
                          }}
                          className='w-4 h-4 accent-primary bg-slate-800 border-slate-600 rounded focus:ring-primary'
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className='flex items-center gap-3'>
                <label className='flex items-center gap-2 text-sm text-slate-300 cursor-pointer'>
                  <input
                    type='checkbox'
                    checked={smtpForm.isActive}
                    onChange={(e) => setSmtpForm({ ...smtpForm, isActive: e.target.checked })}
                    className='w-4 h-4 accent-primary bg-slate-900 border-slate-600 rounded focus:ring-primary'
                  />
                  Conta Ativa
                </label>
              </div>

              <div>
                <label className='block text-sm font-medium text-slate-300 mb-2'>Notas</label>
                <textarea
                  value={smtpForm.notes}
                  onChange={(e) => setSmtpForm({ ...smtpForm, notes: e.target.value })}
                  rows={2}
                  placeholder='Observações sobre esta conta'
                  className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`}
                />
              </div>
        </CascaDeModal>
      )}
    </div>
  );
}
